
import { Observable, of } from "rxjs";
import { Injectable, inject } from "@angular/core";
import { HttpClient } from '@angular/common/http';
import { share, switchMap, map, catchError } from "rxjs/operators";
import { TServiceResult } from "@shared/helper/t-service-result";
import { LocalRepository } from '@shared/helper/local-repository';
import { EBookState } from "@app/pages/books/models/book-state.enum";
import { HttpBaseService } from '@shared/service/base_services/http_base.service';
import { bookViewModel } from "@shared/model/new-models/book-view-model";
import { PageList } from "@shared/model/categoryAndSearch/pageList-model";
import { ShelfItemModel } from "@shared/model/customerModels/shelves-model";
import { UserLibIdsModel } from "@shared/model/new-models/user-lib-ids-model";
import { TermFilterModel } from "@shared/model/categoryAndSearch/term-filter-model";
import { GustShelvesWithBooksAggrigator } from "@app/pages/library/models/guest-shelves-with-book-aggrigator-model";
import { progressModel } from "@shared/model/states/progress.model";
import { BookWithoutReviewModel } from "@shared/model/library/book-without-review.model";
import { BookCustomersModel } from '@app/pages/books/models/book-customer-model';
import { SearchFilterModel } from '@shared/model/categoryAndSearch/search-filter-model';

@Injectable({
  providedIn: "root"
})

export class ShelvesAggrigatorService extends HttpBaseService {

  constructor() {
    const repo = inject(LocalRepository);
    const http = inject(HttpClient);

    super(repo, http, 'v1/shlAggr');
  }

  getGuestShelvesWithBooks(userId: string, TF: TermFilterModel, type: number): Observable<TServiceResult<PageList<GustShelvesWithBooksAggrigator>>> {
    let paramsValues = '';
    if (TF.pgNumber || TF.pgNumber == 0)
      paramsValues = `?PgNumber=${TF.pgNumber}&type=${type}`
    if (TF.pgSize)
      paramsValues = `?PgSize=${TF.pgSize}&type=${type}`
    if ((TF.pgNumber || TF.pgNumber == 0) && TF.pgSize)
      paramsValues = `?PgNumber=${TF.pgNumber}&PgSize=${TF.pgSize}&type=${type}`
    return this.http.get<TServiceResult<PageList<GustShelvesWithBooksAggrigator>>>(`${this.apiUrl}/GetGustShelvesWithBooks/${userId}/${paramsValues}`);
  }

  getGuestLib(userId: string, TF: TermFilterModel, shelfId?: string, bookState?: number): Observable<TServiceResult<PageList<bookViewModel>>> {
    let paramsValues = '';
    TF.searchTerm == null ? TF.searchTerm = '' : null;

    if (shelfId) {
      paramsValues = `?SearchTerm=${TF.searchTerm}&shelfId${shelfId}`;
    }
    if (TF.pgNumber || TF.pgNumber == 0) {
      paramsValues = `?SearchTerm=${TF.searchTerm}&PgNumber=${TF.pgNumber}&bookState=${bookState}`;
    }

    if (TF.pgSize) {
      paramsValues = `?SearchTerm=${TF.searchTerm}&PgSize=${TF.pgSize}&bookState=${bookState}`;
    }

    if ((TF.pgNumber || TF.pgNumber == 0) && TF.pgSize) {
      paramsValues = `?SearchTerm=${TF.searchTerm}&PgNumber=${TF.pgNumber}&PgSize=${TF.pgSize}&bookState=${bookState}`;
    }

    if ((TF.pgNumber || TF.pgNumber == 0) && TF.pgSize && shelfId) {
      paramsValues = `?SearchTerm=${TF.searchTerm}&PgNumber=${TF.pgNumber}&PgSize=${TF.pgSize}&bookState=${bookState}&shelfId=${shelfId}`;
    }

    return this.http.get<TServiceResult<PageList<bookViewModel>>>(`${this.apiUrl}/GetGuestLib/${userId}/${paramsValues}`);
  }

  getGuestLibWithFilter(userId: string, bookState: number, shelfId: string, searchFilter: SearchFilterModel): Observable<TServiceResult<PageList<bookViewModel>>> {
    const params: string = bookState >= 0 ? `?bookState=${bookState}` : shelfId ? `?shelfId=${shelfId}` : '';
    return this.http.post<TServiceResult<PageList<bookViewModel>>>(`${this.apiUrl}/GetGuestLib/${userId}${params}`, searchFilter);
  }

  getGuestShelves(userId: string, termFilter: TermFilterModel): Observable<TServiceResult<PageList<ShelfItemModel>>> {
    const paramsValues = this.TermFilterParams(termFilter);
    return this.http.get<TServiceResult<PageList<ShelfItemModel>>>(`${this.apiUrl}/GetGuestShelves/${userId}${paramsValues}`);
  }

  getGuestShelvesWithFilter(userId: string, searchFilter: SearchFilterModel): Observable<TServiceResult<PageList<ShelfItemModel>>> {
    return this.http.post<TServiceResult<PageList<ShelfItemModel>>>(`${this.apiUrl}/GetGuestShelves/${userId}`, searchFilter);
  }

  getUserLib(TF: TermFilterModel, type: number, shelfId?: string, bookState?: number): Observable<TServiceResult<PageList<bookViewModel>>> {
    let paramsValues = '';
    TF.searchTerm == null ? TF.searchTerm = '' : null;
    if (shelfId)
      paramsValues = `?SearchTerm=${TF.searchTerm}&shelfId${shelfId}`;
    if (TF.pgNumber || TF.pgNumber == 0)
      paramsValues = `?SearchTerm=${TF.searchTerm}&PgNumber=${TF.pgNumber}&bookState=${bookState}&type=${type}`;
    if (TF.pgSize)
      paramsValues = `?SearchTerm=${TF.searchTerm}&PgSize=${TF.pgSize}&bookState=${bookState}&type=${type}`;
    if ((TF.pgNumber || TF.pgNumber == 0) && TF.pgSize)
      paramsValues = `?SearchTerm=${TF.searchTerm}&PgNumber=${TF.pgNumber}&PgSize=${TF.pgSize}&bookState=${bookState}&type=${type}`;
    if ((TF.pgNumber || TF.pgNumber == 0) && TF.pgSize && shelfId)
      paramsValues = `?SearchTerm=${TF.searchTerm}&PgNumber=${TF.pgNumber}&PgSize=${TF.pgSize}&bookState=${bookState}&type=${type}&shelfId=${shelfId}`;
    return this.http.get<TServiceResult<PageList<bookViewModel>>>(`${this.apiUrl}/GetUserLib${paramsValues}`)
      .pipe(
        switchMap((booksResult) => {
          if (!booksResult.hasError) {
            return this.injectMyRateState(booksResult?.result);
          }
          return of(new TServiceResult<PageList<bookViewModel>>(new PageList(), '', null, true, ''))
        })
      );
  }

  getCurrentlyReadingUserLib(termFilter: TermFilterModel): Observable<TServiceResult<PageList<bookViewModel>>> {
    const paramsValues = this.TermFilterParams(termFilter);
    const lastState: any = null
    if (!lastState || lastState.hasError) {
      return this.http.get<TServiceResult<PageList<bookViewModel>>>(`${this.apiUrl}/GetUserLib${paramsValues}&bookState=2&type=1`)
        .pipe(
          map(res => {
            return res
          })
        )
    }
    else {
      return of(lastState)
    }
  }

  getStateList(ids: string[]): Observable<TServiceResult<PageList<UserLibIdsModel>>> {
    return this.getBooksState()
      .pipe(switchMap(result => {
        let data = new TServiceResult<PageList<UserLibIdsModel>>(new PageList(), '', '', false, '');
        if (result?.result?.items) {
          const founds = result?.result?.items.filter(x => ids?.indexOf(x.bookId!) > -1);
          const pl = result.result;
          pl.items = [...founds];
          data = new TServiceResult<PageList<UserLibIdsModel>>(pl, '', '', false, '')
          return of(data);
        }
        return of(data);
      }))

  }

  injectMyRateState(booksResult: PageList<bookViewModel>): Observable<TServiceResult<PageList<bookViewModel>>> {
    return this.getStateList(booksResult?.items.map(x => x.id!))
      .pipe(switchMap(result => {
        let data = new TServiceResult<PageList<bookViewModel>>(new PageList(), '', '', false, '')
        if (result?.result?.items) {
          for (const iterator of booksResult?.items) {
            const b = result?.result?.items.find(x => x.bookId == iterator.id);
            if (b) {
              iterator.stateId = b.bookStateId;
              // iterator.myRate.averageRate = b.myRate;
            }
          }
          data = new TServiceResult<PageList<bookViewModel>>(booksResult, '', '', false, '');
          return of(data);
        }
        return of(data);
      }))
  }


  getGuestDefualtShelves(userId: string): Observable<TServiceResult<PageList<GustShelvesWithBooksAggrigator>>> {
    return this.http.get<TServiceResult<PageList<GustShelvesWithBooksAggrigator>>>(`${this.apiUrl}/GetGuestDefaultShelves/${userId}`);
  }

  getBooksState(): Observable<TServiceResult<PageList<UserLibIdsModel>>> {
    return this.http.get<TServiceResult<PageList<UserLibIdsModel>>>(`${this.apiUrl}/GetUserLibIds`)
      .pipe(
        share()
      );
  }


  getBookState(bookId: string): Observable<TServiceResult<EBookState>> {
    // * only to fire the !Important getBooksStateUser
    return this.getBooksState()
      .pipe(

        switchMap(booksStateresult => {
          let bookState = 0;
          let hasError = false;
          if (booksStateresult?.result?.items) {
            const lib = booksStateresult?.result?.items;
            if ((lib).findIndex(x => x.bookId == bookId) >= 0) {
              const found = (lib).find(x => x.bookId == bookId);
              bookState = found?.bookStateId || 0;
              if (found?.bookStateId) {
                hasError = false;
              }
              else {
                hasError = true;
              }
            }
          }
          return of(new TServiceResult<EBookState>(bookState, '', '', hasError, ''));
        })
      );

  }

  getProgress(activityId: string): Observable<TServiceResult<progressModel>> {
    return this.http.get<TServiceResult<progressModel>>(`${this.apiUrl}/GetProgress/${activityId}`);
  }

  getGuestBooksWithoutReview(userId: string): Observable<TServiceResult<PageList<BookWithoutReviewModel>>> {
    return this.http.get<TServiceResult<PageList<BookWithoutReviewModel>>>(`${this.apiUrl}/GetGuestBooksWithoutReview/${userId}`)
  }

  getGuestDefaultShelvesWithBooks(userId: string, termFilter: TermFilterModel): Observable<TServiceResult<PageList<GustShelvesWithBooksAggrigator>>> {
    const paramsValues = this.TermFilterParams(termFilter);
    return this.http.get<TServiceResult<PageList<GustShelvesWithBooksAggrigator>>>(`${this.apiUrl}/GetGuestDefaultShelvesWithBook/${userId}/${paramsValues}`)
      .pipe(
        switchMap(x => {
          if (!x?.hasError && x.result.items) {
            x.result.items = x.result.items.filter(shelf => shelf.title !== 'WantToBuy');
          }
          return of(x);
        }),
        catchError(() => {
          return of(new TServiceResult<PageList<GustShelvesWithBooksAggrigator>>(new PageList(), '', null, true, ''))
        })
      )

  }

  GetBookCustomersByBookId(state: number, bookId: string, termFilter: TermFilterModel): Observable<TServiceResult<PageList<BookCustomersModel>>> {
    const bookState = `?bookState=${state}`;
    const paramsValues = `&SearchTerm=${termFilter.searchTerm}&PgNumber=${termFilter.pgNumber}&PgSize=${termFilter.pgSize}`;

    return this.http.get<TServiceResult<PageList<BookCustomersModel>>>(`${this.apiUrl}/GetBookCustomersByBookId/${bookId}${bookState}${paramsValues}`);
  }

}
